import { AppConfigType } from '@appTypes/AppConfigType';
import { CurrentUser } from '@appTypes/CurrentUser';
import { NotificationType } from '@enums/NotificationType';
import { User, UserManager, WebStorageStateStore } from 'oidc-client-ts';

const handleUrlRedirections = (clientRoot: string): void => {
  if (window.location.href.indexOf('customizedNotification') > -1) {
    const pushOrSmsNotificationUrl = window.location.href;
    localStorage.setItem('pushOrSmsNotificationUrl', pushOrSmsNotificationUrl);
  }

  if (
    window.location.href.indexOf('dealsheet') > -1 &&
    window.location.href.indexOf('notificationType') > -1 &&
    window.location.href.indexOf('customizedNotification') === -1
  ) {
    const currentUrl = new URL(window.location.href);
    const clientRootUrl = new URL(clientRoot);

    // Only proceed if we're on the same origin as clientRoot
    if (currentUrl.origin !== clientRootUrl.origin) {
      console.error('Security violation: Current origin does not match clientRoot origin');
      return;
    }

    // Get the notification type from the URL
    const notificationType = currentUrl.searchParams.get('notificationType');

    // Validate the notification type
    if (
      notificationType &&
      Object.values(NotificationType).includes(notificationType as NotificationType)
    ) {
      // Create a map of allowed redirection paths
      const allowedRedirects = {
        [NotificationType.ServiceAppointment]: `${clientRoot}/dealsheet/customizedNotification/${NotificationType.ServiceAppointment}`,
        [NotificationType.AutoActivatedBySMm]: `${clientRoot}/dealsheet/customizedNotification/${NotificationType.AutoActivatedBySMm}`,
        [NotificationType.FinalPayment]: `${clientRoot}/dealsheet/customizedNotification/${NotificationType.FinalPayment}`,
        [NotificationType.Birthday]: `${clientRoot}/dealsheet/customizedNotification/${NotificationType.Birthday}`,
        [NotificationType.Anniversary]: `${clientRoot}/dealsheet/customizedNotification/${NotificationType.Anniversary}`,
        [NotificationType.MdlServiceArrival]: `${clientRoot}/dealsheet/customizedNotification/${NotificationType.MdlServiceArrival}`,
        [NotificationType.EmailCampaignClick]: `${clientRoot}/dealsheet/customizedNotification/${NotificationType.EmailCampaignClick}`,
        [NotificationType.AssignedDeal]: `${clientRoot}/dealsheet/customizedNotification/${NotificationType.AssignedDeal}`,
        [NotificationType.PrivateIncentive]: `${clientRoot}/dealsheet/customizedNotification/${NotificationType.PrivateIncentive}`,
        [NotificationType.ConquestCampaignClick]: `${clientRoot}/dealsheet/customizedNotification/${NotificationType.ConquestCampaignClick}`,
        [NotificationType.CampaignQrCodeScan]: `${clientRoot}/dealsheet/customizedNotification/${NotificationType.CampaignQrCodeScan}`,
        [NotificationType.CampaignUrlClick]: `${clientRoot}/dealsheet/customizedNotification/${NotificationType.CampaignUrlClick}`,
        [NotificationType.AssignedMarketConquestDeal]: `${clientRoot}/dealsheet/customizedNotification/${NotificationType.AssignedMarketConquestDeal}`,
        [NotificationType.ShowroomUrlQrCodeScan]: `${clientRoot}/dealsheet/customizedNotification/${NotificationType.ShowroomUrlQrCodeScan}`,
        [NotificationType.ShowroomUrlClicked]: `${clientRoot}/dealsheet/customizedNotification/${NotificationType.ShowroomUrlClicked}`,
        [NotificationType.ShowroomOfferSubmitted]: `${clientRoot}/dealsheet/customizedNotification/${NotificationType.ShowroomOfferSubmitted}`,
      };

      // Check if we have a predefined path for this notification type
      if (allowedRedirects[notificationType as NotificationType]) {
        window.location.href = allowedRedirects[notificationType as NotificationType];
      } else {
        console.error('No predefined redirect path for notification type:', notificationType);
      }
    } else {
      console.error('Invalid notificationType:', notificationType);
    }
  }
};

export const isAuth0 = (authority: string): boolean => {
  return authority.includes('auth0') || authority.includes('auth.automotivemastermind.com');
};

const getExtraQueryParams = (authority: string, apiUrl: string): Record<string, string> => {
  let extraQueryParams = {};

  //Checks if pwa-instance-id is available in local storage for PWA
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    extraQueryParams = window.matchMedia('(display-mode: standalone)').matches
      ? { pwaInstanceId: localStorage.getItem('pwa-instance-id') || 'true' }
      : {};
  }

  if (isAuth0(authority)) {
    extraQueryParams['audience'] = `${apiUrl}/`;
  }

  return extraQueryParams;
};

export const createUserManager = (): UserManager => {
  const config = localStorage.getItem('Config');

  if (config !== null) {
    const appConfig: AppConfigType = JSON.parse(config);
    const clientRoot = window.location.origin;
    const authority = appConfig.stsAuthority;
    const apiUrl = appConfig.apiUrl;

    handleUrlRedirections(clientRoot);

    const userManagerSettings = {
      authority: appConfig.stsAuthority,
      client_id: appConfig.clientId,
      redirect_uri: `${clientRoot}/auth-callback`,
      automaticSilentRenew: false,
      response_type: 'code',
      scope: 'openid profile am.gateway',
      post_logout_redirect_uri: `${clientRoot}`,
      userStore: new WebStorageStateStore({ store: localStorage }),
      extraQueryParams: getExtraQueryParams(authority, apiUrl),
    };

    return new UserManager(userManagerSettings);
  } else {
    return userManager;
  }
};

let userManager = new UserManager({
  authority: 'https://shared-identity.stg.kube.network',
  client_id: 'localDashboard',
  redirect_uri: 'http://localhost:3000/auth-callback',
  automaticSilentRenew: false,
  response_type: 'code',
  scope: 'openid profile am.gateway',
  post_logout_redirect_uri: 'http://localhost:3000',
  userStore: new WebStorageStateStore({ store: localStorage }),
  extraQueryParams: getExtraQueryParams(
    'https://shared-identity.stg.kube.network',
    'https://shared-gateway.stg.kube.network',
  ),
});

export const setUserManager = (updatedUserManager: UserManager): void => {
  userManager = updatedUserManager;
};

export const getUserManager = (): UserManager => {
  return userManager;
};

export const getUser = async (): Promise<User | null> => {
  const user: User | null = await userManager.getUser();
  return user;
};

export const isUserLoggedIn = async (): Promise<boolean> => {
  const user: User | null = await userManager.getUser();
  return user != null && !user.expired;
};

export const loginRedirect = async (): Promise<void> => {
  return userManager.signinRedirect();
};

export const loginCallback = async (): Promise<User | undefined> => {
  try {
    return userManager.signinCallback();
  } catch (exception) {
    // if user tries to go sign in Oidc page directly then catch error
    await clearState();
    throw new Error('login Error');
  }
};

export const logoutRedirect = async (): Promise<void> => {
  const user: User | null = await userManager.getUser();
  clearState();
  if (user != null) {
    await userManager.signoutRedirect({ id_token_hint: user?.id_token });
  }
};

export const clearState = async (): Promise<void> => {
  await userManager.clearStaleState();
  await userManager.removeUser();
  const pwaInstance = localStorage.getItem('pwa-instance-id');
  const isSharedDevice = localStorage.getItem('isSharedPwaDevice');
  localStorage.clear();
  if (pwaInstance) {
    localStorage.setItem('pwa-instance-id', pwaInstance);
  }
  if (isSharedDevice) {
    localStorage.setItem('isSharedPwaDevice', isSharedDevice);
  }
};

export const parseJwt = (token: string): CurrentUser => {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join(''),
  );

  return JSON.parse(jsonPayload);
};

export const getAuthorizationHeaderValue = (): string | null => {
  const user = JSON.parse(localStorage.getItem('user') ?? '');
  return user ? `${user.token_type} ${user.access_token}` : null;
};

export const getParsedToken = (): CurrentUser => {
  const token = getAuthorizationHeaderValue();
  const parsedToken = parseJwt(token ?? '');
  // convert strings in the token to their correct types
  parsedToken.isPrimaryUser =
    parsedToken.isPrimaryUser && parsedToken.isShadowLogin !== 'True' ? true : false;
  parsedToken.permissionGrant = parseInt(<string>parsedToken.permissionGrant, 10);

  return parsedToken;
};

export const completeAuthentication = (): Promise<string> => {
  const user = JSON.parse(localStorage.getItem('user') ?? '{}');

  return Promise.resolve(`${user?.token_type} ${user.access_token}`);
};

export default userManager;
